import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { BookUser, Briefcase, Contact } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormActions, FormSection } from '@/components/forms/FormSection'
import { FormField } from '@/components/forms/FormField'
import { ImageUpload } from '@/components/forms/ImageUpload'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { subjectsQuery } from '@/features/academic/api'
import { departmentsQuery, designationsQuery, staffQuery, useSaveStaff } from '@/features/staff/api'
import { staffSchema, type StaffFormInput, type StaffFormOutput } from '@/features/staff/schemas'
import { EMPLOYMENT_TYPES, type StaffMember } from '@/features/staff/types'
import { BLOOD_GROUPS } from '@/features/students/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { humanize } from '@/utils/format'

const FIELDS = Object.keys(staffSchema.shape)

function defaults(m?: StaffMember): StaffFormInput {
  return {
    firstName: m?.firstName ?? '',
    lastName: m?.lastName ?? '',
    gender: m?.gender ?? 'female',
    dateOfBirth: m?.dateOfBirth ?? '',
    bloodGroup: m?.bloodGroup ?? '',
    email: m?.email ?? '',
    phone: m?.phone ?? '',
    address: m?.address ?? '',
    qualification: m?.qualification ?? '',
    departmentId: m?.department ? String(m.department.id) : '',
    designationId: m?.designation ? String(m.designation.id) : '',
    primarySubjectId: m?.primarySubject ? String(m.primarySubject.id) : '',
    employmentType: m?.employmentType ?? 'permanent',
    joiningDate: m?.joiningDate ?? new Date().toISOString().slice(0, 10),
    basicSalary: m?.basicSalary ?? '',
    photoFileId: null,
  }
}

function StaffForm({ member }: { member?: StaffMember }) {
  const { t } = useTranslation('staff')
  const navigate = useNavigate()
  const save = useSaveStaff()
  const departments = useQuery(departmentsQuery())
  const designations = useQuery(designationsQuery())
  const subjects = useQuery(subjectsQuery())
  const {
    register,
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<StaffFormInput, unknown, StaffFormOutput>({
    resolver: zodResolver(staffSchema),
    defaultValues: defaults(member),
  })
  const name = `${watch('firstName')} ${watch('lastName')}`.trim()

  const onSubmit = handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync({ ...values, id: member?.id })
      toast.success(
        member ? t('Changes saved') : t('Staff member hired'),
        `${saved.fullName} · ${saved.employeeCode}`,
      )
      void navigate(`/admin/staff/${saved.id}`)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  const opts = (items: { id: number; name: string }[] | undefined) =>
    (items ?? []).map((i) => ({ value: i.id, label: i.name }))

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-6">
      <FormSection title={t('Personal details')} icon={<BookUser />} columns={3}>
        <div className="sm:col-span-2 lg:col-span-3">
          <Controller
            control={control}
            name="photoFileId"
            render={({ field }) => (
              <ImageUpload name={name} currentUrl={member?.photoUrl ?? null} onUploaded={field.onChange} />
            )}
          />
        </div>
        <FormField label={t('First name')} error={errors.firstName?.message} required>
          <Input {...register('firstName')} />
        </FormField>
        <FormField label={t('Last name')} error={errors.lastName?.message} required>
          <Input {...register('lastName')} />
        </FormField>
        <FormField label={t('Gender')} error={errors.gender?.message} required>
          <Select
            options={['female', 'male', 'other'].map((g) => ({ value: g, label: humanize(g) }))}
            {...register('gender')}
          />
        </FormField>
        <FormField label={t('Date of birth')} error={errors.dateOfBirth?.message}>
          <Input type="date" {...register('dateOfBirth')} />
        </FormField>
        <FormField label={t('Blood group')} error={errors.bloodGroup?.message}>
          <Select
            placeholder={t('Unknown')}
            options={BLOOD_GROUPS.map((b) => ({ value: b, label: b }))}
            {...register('bloodGroup')}
          />
        </FormField>
        <FormField label={t('Qualification')} error={errors.qualification?.message}>
          <Input placeholder={t('e.g. M.Sc. Mathematics')} {...register('qualification')} />
        </FormField>
      </FormSection>

      <FormSection title={t('Contact')} icon={<Contact />}>
        <FormField label={t('Email')} error={errors.email?.message}>
          <Input type="email" {...register('email')} />
        </FormField>
        <FormField label={t('Phone')} error={errors.phone?.message}>
          <Input type="tel" {...register('phone')} />
        </FormField>
        <FormField label={t('Address')} error={errors.address?.message} className="sm:col-span-2">
          <Textarea rows={2} {...register('address')} />
        </FormField>
      </FormSection>

      <FormSection title={t('Employment')} icon={<Briefcase />} columns={3}>
        <FormField label={t('Designation')} error={errors.designationId?.message}>
          <Select placeholder={t('None')} options={opts(designations.data)} {...register('designationId')} />
        </FormField>
        <FormField label={t('Department')} error={errors.departmentId?.message}>
          <Select placeholder={t('None')} options={opts(departments.data)} {...register('departmentId')} />
        </FormField>
        <FormField
          label={t('Main subject')}
          error={errors.primarySubjectId?.message}
          hint={t('For teaching staff')}
        >
          <Select placeholder={t('None')} options={opts(subjects.data)} {...register('primarySubjectId')} />
        </FormField>
        <FormField label={t('Employment type')} error={errors.employmentType?.message} required>
          <Select
            options={EMPLOYMENT_TYPES.map((type) => ({ value: type, label: humanize(type) }))}
            {...register('employmentType')}
          />
        </FormField>
        <FormField label={t('Joining date')} error={errors.joiningDate?.message}>
          <Input type="date" {...register('joiningDate')} />
        </FormField>
        <FormField
          label={t('Basic salary')}
          error={errors.basicSalary?.message}
          hint={t('Monthly, in the school currency')}
        >
          <Input type="number" min={0} step="0.01" inputMode="decimal" {...register('basicSalary')} />
        </FormField>
      </FormSection>

      <FormActions>
        <Button
          variant="secondary"
          onClick={() => void navigate(member ? `/admin/staff/${member.id}` : '/admin/staff')}
          disabled={isSubmitting}
        >
          {t('Cancel')}
        </Button>
        <Button type="submit" loading={isSubmitting}>
          {member ? t('Save changes') : t('Hire staff member')}
        </Button>
      </FormActions>
    </form>
  )
}

export function StaffFormPage() {
  const { id } = useParams()
  const { t } = useTranslation('staff')
  const editing = id !== undefined
  const member = useQuery({ ...staffQuery(Number(id)), enabled: editing })
  useDocumentTitle(editing ? 'Edit staff member' : 'Hire staff')

  if (editing && member.isPending) return <LoadingState />
  if (editing && member.error) return <ErrorState onRetry={() => void member.refetch()} />

  return (
    <>
      <PageHeader
        title={editing ? t('Edit {{name}}', { name: member.data?.fullName ?? '' }) : t('Hire a staff member')}
        description={
          editing ? member.data?.employeeCode : t('An employee ID is generated automatically when you save.')
        }
        breadcrumbs={[
          { label: t('Staff'), to: '/admin/staff' },
          ...(editing && member.data
            ? [{ label: member.data.fullName, to: `/admin/staff/${member.data.id}` }]
            : []),
          { label: editing ? t('Edit') : t('Hire') },
        ]}
        homeHref="/admin"
      />
      <StaffForm key={member.data?.id ?? 'new'} member={member.data} />
    </>
  )
}
